import { UserProfileType } from '../peekapak-types/DataProtocolTypes';
import { hasUserMadeChoice } from './SurveyChoiceHelpers';
import { getLocalStorage } from './aaa';

/**
 * TELEMETRY DATA CHUNKING STRATEGY
 *
 * Mixpanel has a 255-byte limit per string property. To stay within limits while
 * capturing complete decision context, we split SurveyPromptDecisionData across
 * 3 chunks based on priority:
 *
 * CHUNK 0 - CRITICAL (Priority 1, ~150 bytes):
 * Fields that answer "What decision was made and why?"
 * - shouldPrompt: boolean - Was the decision to show survey-choice page?
 * - reason: string - Human-readable explanation of the decision
 * - decidedBy: 'AuthenticatedRoute' | 'sendUserToRoleHomePage' - Which code path made the decision
 * - hasBackendEligibilityFlag: boolean - Did backend set isShowSurvey=true flag?
 *
 * CHUNK 1 - IMPORTANT (Priority 2, ~180 bytes):
 * Fields that answer "Who and which campaign?"
 * - userId: string - User identifier (UUID format)
 * - campaignId: string | undefined - Survey campaign identifier
 * - hasUserMadeRecentChoice: boolean - Is there a recent skip/take choice in localStorage?
 * - currentPath: string - URL path where decision was made
 *
 * CHUNK 2 - CONTEXT (Priority 3, ~150 bytes):
 * Fields that provide debugging details
 * - hasSurveyParameters: boolean - Did backend provide survey parameters?
 * - choiceData: object | null - Details about user's choice (timestamp, expiry, type)
 *
 * This prioritization ensures that even if later chunks fail to send or are truncated
 * by Mixpanel, the most critical information (chunk 0) is always captured.
 *
 * CENTRALIZED COMPILATION:
 * All data compilation and chunking happens in this file only. Callers receive
 * pre-compiled, pre-chunked data and forward it without manipulation.
 */

/**
 * Data shape for survey prompt decision telemetry
 * Used with TelemetryEvent where telemetryName = 'surveyPromptDecision'
 *
 * This typed interface ensures we send consistent, structured data
 * even though it's stored as JSON strings across multiple telemetry fields
 */
export interface SurveyPromptDecisionData {
  shouldPrompt: boolean;
  reason: string;
  decidedBy: 'AuthenticatedRoute' | 'sendUserToRoleHomePage';
  userId: string;
  campaignId: string | undefined;
  currentPath: string;
  hasBackendEligibilityFlag: boolean;
  hasSurveyParameters: boolean;
  hasUserMadeRecentChoice: boolean;
  choiceData?: {
    choice: 'take' | 'skip';
    timestamp: number;
    expiresAt: number;
    timeUntilExpiry: number;
  } | null;
}

/**
 * Data shape for survey chain detection telemetry
 * Used with TelemetryEvent where telemetryName = 'surveyChainDetection'
 *
 * Tracks the success/failure of detecting chained surveys after completion.
 * Helps monitor backend processing time and identify issues with survey chains.
 *
 * SUCCESS: New campaign detected within retry attempts
 * FAILURE: Max retries reached without new campaign, or backend says no more surveys
 */
export interface SurveyChainDetectionData {
  success: boolean; // Was a new chained campaign detected?
  attemptsNeeded: number; // Number of attempts until result (1-6)
  totalTimeMs: number; // Total time from submission to final result
  oldCampaignId: string; // Campaign ID that was just completed
  newCampaignId: string | undefined; // New campaign ID (if success=true)
  backendHadMoreSurveys: boolean; // Was isShowSurvey=true after retries?
  maxRetriesReached: boolean; // Did we hit the retry limit without success?
}

/**
 * Decision object returned by survey prompt decision function
 *
 * Contains all context about why the decision was made, in two formats:
 *
 * 1. telemetryData: Complete data object
 *    - Used for console logging (human-readable, complete structure)
 *    - Contains all fields in single object
 *
 * 2. telemetryDataChunks: Array of 3 data chunks
 *    - Used for analytics (Mixpanel)
 *    - Pre-split to respect Mixpanel's 255-byte per-property limit
 *    - [chunk0, chunk1, chunk2] = [critical, important, context]
 *
 * The split is done automatically by decideShouldPromptForSurvey() using
 * splitDecisionDataForTelemetry(), so callers don't need to know about
 * the chunking strategy.
 */
export interface SurveyPromptDecision {
  shouldPrompt: boolean;
  reason: string;
  decidedBy: 'AuthenticatedRoute' | 'sendUserToRoleHomePage';
  telemetryData: SurveyPromptDecisionData; // Complete data for console logging
  telemetryDataChunks: Record<string, any>[]; // Split into 3 chunks for analytics
  timestamp: number;
}

/**
 * Split survey prompt decision data into chunks for telemetry
 *
 * CENTRALIZED DATA CHUNKING - This is the ONLY place where data is split.
 * All callers receive the pre-split chunks and forward them as-is.
 *
 * Mixpanel has a 255-byte limit per string property. This function splits
 * the complete decision data into 3 prioritized chunks to stay within limits.
 *
 * CHUNK ALLOCATION (Priority-Based):
 *
 * Chunk 0 (Critical - ~150 bytes max):
 * - shouldPrompt, reason, decidedBy, hasBackendEligibilityFlag
 * - Most important: tells us WHAT decision was made and WHY
 * - Always preserved even if other chunks fail
 *
 * Chunk 1 (Important - ~180 bytes max):
 * - userId, campaignId, hasUserMadeRecentChoice, currentPath
 * - Identification: tells us WHO and WHICH CAMPAIGN
 * - Essential for filtering and correlation in Mixpanel
 *
 * Chunk 2 (Context - ~150 bytes max):
 * - hasSurveyParameters, choiceData
 * - Details: provides debugging context and choice metadata
 * - Helpful but not critical
 *
 * @param data - Complete decision data
 * @returns Array of 3 data chunks for telemetryData0, telemetryData1, telemetryData2
 */
function splitDecisionDataForTelemetry(data: SurveyPromptDecisionData): Record<string, any>[] {
  // Chunk 0: Critical decision data (Priority 1)
  // What was decided and why? Most important for understanding the decision.
  const chunk0 = {
    shouldPrompt: data.shouldPrompt,
    reason: data.reason,
    decidedBy: data.decidedBy,
    hasBackendEligibilityFlag: data.hasBackendEligibilityFlag,
  };

  // Chunk 1: Important identification and context (Priority 2)
  // Who is this about and which campaign? Essential for filtering and correlation.
  const chunk1 = {
    userId: data.userId,
    campaignId: data.campaignId,
    hasUserMadeRecentChoice: data.hasUserMadeRecentChoice,
    currentPath: data.currentPath,
  };

  // Chunk 2: Detailed context and metadata (Priority 3)
  // Additional debugging details and choice information.
  const chunk2 = {
    hasSurveyParameters: data.hasSurveyParameters,
    choiceData: data.choiceData,
  };

  return [chunk0, chunk1, chunk2];
}

/**
 * Centralized decision: Should we prompt user for survey?
 *
 * IMPORTANT DISTINCTION - Eligibility vs Prompting:
 *
 * ELIGIBILITY (Backend Responsibility):
 * - Backend determines which surveys user CAN complete
 * - Backend checks: completion history, campaign targeting, grade level, school/district
 * - Backend output: userProfile.flags.isShowSurvey = true/false
 * - Backend output: userProfile.surveyParameters (campaign details)
 *
 * SHOULD PROMPT (Frontend Responsibility - This Function):
 * - Decides whether to SHOW survey-choice page NOW
 * - Checks: backend eligibility flag + user's recent skip/take decisions
 * - Does NOT re-check completion history (backend already handled that)
 * - Output: SurveyPromptDecision object
 *
 * FRONTEND CONSIDERATIONS:
 *
 * 1. Skip Decisions (2-hour window):
 *    - When user clicks "Skip for Now", choice stored in localStorage
 *    - For 2 hours, we don't prompt again even if backend says eligible
 *    - After 2 hours, choice expires and we can prompt again
 *    - Skip is per-campaign: skipping Survey A doesn't affect Survey B
 *
 * 2. Chained Surveys:
 *    - After completing Survey A, backend may set isShowSurvey=true for Survey B
 *    - This function checks if user should be prompted for Survey B
 *    - Since it's a different campaign ID, skip of Survey A doesn't block Survey B
 *
 * SIMPLIFIED LOGIC (2 checks only):
 * 1. Is backend eligibility flag set? (isShowSurvey + surveyParameters)
 * 2. Has user made recent choice for this campaign? (localStorage, 2h window)
 *
 * @param userProfile - User profile (should be current/fresh from Redux)
 * @param context - Context about where decision is being made
 * @returns Decision object with full context for logging and debugging
 */
export function decideShouldPromptForSurvey(
  userProfile: UserProfileType | null,
  context: {
    currentPath: string;
    decidedBy: 'AuthenticatedRoute' | 'sendUserToRoleHomePage';
  },
): SurveyPromptDecision {
  // Initialize telemetry data with base values
  const baseTelemetry: SurveyPromptDecisionData = {
    shouldPrompt: false,
    reason: '',
    decidedBy: context.decidedBy,
    userId: userProfile?.userId || 'unknown',
    campaignId: userProfile?.surveyParameters?.campaignId,
    currentPath: context.currentPath,
    hasBackendEligibilityFlag: !!userProfile?.flags?.isShowSurvey,
    hasSurveyParameters: !!userProfile?.surveyParameters,
    hasUserMadeRecentChoice: false,
    choiceData: null,
  };

  // ===== Check 1: Backend Eligibility Flag =====
  // Backend sets isShowSurvey=true when user is eligible
  // Backend has already checked completion history, targeting, etc.
  if (!userProfile?.flags?.isShowSurvey) {
    const telemetryData: SurveyPromptDecisionData = {
      ...baseTelemetry,
      shouldPrompt: false,
      reason: 'Backend eligibility flag not set (isShowSurvey=false or missing)',
    };

    return {
      shouldPrompt: false,
      reason: 'Backend eligibility flag not set (isShowSurvey=false or missing)',
      decidedBy: context.decidedBy,
      telemetryData,
      telemetryDataChunks: splitDecisionDataForTelemetry(telemetryData),
      timestamp: Date.now(),
    };
  }

  // ===== Check 2: Survey Parameters =====
  // Backend should provide parameters when setting isShowSurvey=true
  // If missing, it indicates a backend inconsistency
  if (!userProfile.surveyParameters) {
    const telemetryData: SurveyPromptDecisionData = {
      ...baseTelemetry,
      shouldPrompt: false,
      reason: 'Survey parameters missing despite isShowSurvey=true',
    };

    return {
      shouldPrompt: false,
      reason: 'Survey parameters missing despite isShowSurvey=true',
      decidedBy: context.decidedBy,
      telemetryData,
      telemetryDataChunks: splitDecisionDataForTelemetry(telemetryData),
      timestamp: Date.now(),
    };
  }

  const userId = userProfile.userId;
  const campaignId = userProfile.surveyParameters.campaignId;

  // Update telemetry with actual values
  baseTelemetry.userId = userId;
  baseTelemetry.campaignId = campaignId;

  // ===== Check 3: User's Recent Choice =====
  // If user recently clicked "Skip" or "Take Survey" (within 2 hours), respect that
  // This is a frontend-only state - backend doesn't track skip decisions
  baseTelemetry.hasUserMadeRecentChoice = hasUserMadeChoice(userId, campaignId);

  if (baseTelemetry.hasUserMadeRecentChoice) {
    // Get the actual choice data for detailed logging
    try {
      const allChoices = JSON.parse(getLocalStorage('surveyChoices') || '{}');
      const userChoices = allChoices[userId] || {};
      const choice = userChoices[campaignId];

      if (choice) {
        baseTelemetry.choiceData = {
          choice: choice.choice,
          timestamp: choice.timestamp,
          expiresAt: choice.expiresAt,
          timeUntilExpiry: Math.max(0, choice.expiresAt - Date.now()),
        };
      }
    } catch (error) {
      console.error('[decideShouldPromptForSurvey] Error reading choice data for logging:', error);
    }

    const choiceType = baseTelemetry.choiceData?.choice || 'unknown';
    const minutesRemaining = baseTelemetry.choiceData
      ? Math.ceil(baseTelemetry.choiceData.timeUntilExpiry / (60 * 1000))
      : 0;

    const telemetryData: SurveyPromptDecisionData = {
      ...baseTelemetry,
      shouldPrompt: false,
      reason: `User ${choiceType} recently (${minutesRemaining}m remaining in 2h window)`,
    };

    return {
      shouldPrompt: false,
      reason: `User ${choiceType} recently (${minutesRemaining}m remaining in 2h window)`,
      decidedBy: context.decidedBy,
      telemetryData,
      telemetryDataChunks: splitDecisionDataForTelemetry(telemetryData),
      timestamp: Date.now(),
    };
  }

  // ===== All Checks Passed =====
  // Backend says user is eligible, and user hasn't made a recent choice
  // We should prompt them for the survey
  const telemetryData: SurveyPromptDecisionData = {
    ...baseTelemetry,
    shouldPrompt: true,
    reason: 'Backend eligible and no recent user choice - should prompt',
  };

  return {
    shouldPrompt: true,
    reason: 'Backend eligible and no recent user choice - should prompt',
    decidedBy: context.decidedBy,
    telemetryData,
    telemetryDataChunks: splitDecisionDataForTelemetry(telemetryData),
    timestamp: Date.now(),
  };
}
